import type { AgendaItem, Webcast, Session } from '@graphql/gql/graphql';
import compareAgendaItems from './compareAgendaItems';
import { getUpdatedWebcastStreamStatus } from './sessionUtils/getUpdatedWebcastStreamStatus';

/**
 * Compare a list of agenda items to locally enrolled sessions,
 * overwrite with the local data if it exists, otherwise return
 * the entire list. Will also overwrite sessions with updated
 * webcast status. Use showOnlyAgendaItems to return only
 * sessions that exist in the list of agenda items.
 */
const updateSessions = <T extends { id: string; webcast?: Webcast; enrolled?: boolean }>(
  agendaItems: T[] = [],
  enrolledSessions: Session[] = [],
  updatedWebcastsBySession: { [key: string]: Webcast } = {},
  filterEnrolledSessionsCallBack: (agendaItem: AgendaItem | Session) => boolean = () => true
): T[] => {
  if (enrolledSessions.length === 0 && Object.keys(updatedWebcastsBySession).length === 0) {
    return [...agendaItems].sort(compareAgendaItems);
  }
  const filteredEnrolledSessions = enrolledSessions.filter(filterEnrolledSessionsCallBack);

  const allItems = [...agendaItems, ...filteredEnrolledSessions];
  const mergedAgendaItems = allItems.reduce((acc, current) => {
    const updatedSessionWebcast = updatedWebcastsBySession[current.id]
      ? {
          ...current,
          webcast: getUpdatedWebcastStreamStatus(
            current?.webcast,
            updatedWebcastsBySession[current.id]?.player?.stream?.status
          )
        }
      : current;
    // Check if the current session already exists in the array
    const occurs = acc.reduce((indexAcc, session, i) => {
      return session.id === current.id ? i : indexAcc;
    }, -1);

    // If there is a local version of a session,
    if (occurs >= 0) {
      // Overwrite the original session
      // Enrolled session state is storing session data in current locale, if locale is changed, agenda items brings translated sessions data but enrolled session state
      // contains the name in the previous language. So in case of match, copy flags from enrolled sessions state, keeping the latest Session name, Session description
      // Session category, obtained from agenda item
      const { name, description, category } = acc[occurs];
      // eslint-disable-next-line no-param-reassign
      acc[occurs] = {
        ...acc[occurs],
        ...updatedSessionWebcast,
        name,
        description,
        category
      };
      return acc;
    }

    acc.push(updatedSessionWebcast);

    return acc;
  }, []);

  return mergedAgendaItems.sort(compareAgendaItems);
};

export default updateSessions;
